import type { ColorMode } from "@/components/layouts/colorModeScript";
import { getExposureStatus } from "@/features/collection_home";
import { ReturnPeriod } from "@/features/pe_aldd";
import { ensureGradedRiskValue } from "@/features/risk_bands";
import { bandFillColor, toExposureBand, toRiskBand, type MarkerBand } from "../bands";
import type { AnalysisResultsTab, MapDisplayPoint } from "../types";

interface MarkerBandArgs {
  point: MapDisplayPoint;
  activeTab: AnalysisResultsTab;
  displayYear?: number;
  displayReturnPeriod: ReturnPeriod;
}

/**
 * Which band a point belongs to. Everything that groups, ranks or filters
 * points reads this; only painting reads the colour it maps to.
 */
export const resolveMarkerBand = ({
  point,
  activeTab,
  displayYear,
  displayReturnPeriod,
}: MarkerBandArgs): MarkerBand => {
  if (point.markerStyle?.band) {
    return point.markerStyle.band;
  }

  // A caller forced a raw colour without saying what it means.
  if (point.markerStyle?.fillColor) {
    return "custom";
  }

  if (activeTab === "exposure") {
    return toExposureBand(getExposureStatus(point.response, displayReturnPeriod));
  }

  let rating = point.response?.end_of_mortgage_term?.rating;
  if (displayYear && "ratings" in point.response) {
    for (const { year, rating: yearlyRating } of point.response.ratings) {
      if (year === displayYear) {
        rating = yearlyRating;
        break;
      }
    }
  }

  return toRiskBand(rating ? ensureGradedRiskValue(rating) : "?");
};

export const resolveMarkerFillColor = (args: MarkerBandArgs, mode: ColorMode): string =>
  args.point.markerStyle?.fillColor ?? bandFillColor(resolveMarkerBand(args), mode);
